import { round } from "lodash";
import { useTranslation } from "react-i18next";
import {
  mapParameter,
  parameterName,
} from "../../../../parts/other-exercise/CustomParametersSets";
import PRBadge from "../../../../parts/PRBadge";
import SvgIcon from "../../../../parts/SvgIcon";
import TrendIndicator from "../../../../parts/TrendIndicator";
import { useRillContext } from "../../../../rill/RillReact/share/base";
import {
  formattedTarget,
  getMetaCounts,
  getSetCounts,
  getVelocityValue,
  outcomesAreEqual,
  shouldHaveE1RM,
} from "../../../../utils/actuals";
import {
  getComplexSetOutcomeReps,
  getComplexSetReps,
} from "../../../../utils/complex";
import convertToRiR from "../../../../utils/converters/convert-intensity";
import { isPROutcome } from "../../../../utils/personal-records";
import ExerciseVideoLine from "../../program-builder/components/ExerciseVideoLine";
import CommentComponent from "./CommentComponent";

const {
  PARAMETER_OPTIONS: { ROUNDS, SETS, LOAD, RPE, RPEAMRAP, FREEFORM },
} = require("../../../../constants");

// `showDraftCircle`: the day view's marker for a round that is neither
// completed nor missed - a drafted set (a value was tracked with
// auto-complete off, never confirmed) or a round with no outcome at all
// (blank alongside sibling rounds that do have one, which reads as broken
// if left empty). A plain outlined circle rather than an icon, since neither
// the tick nor the info-circle apply. Week and program-builder callers leave
// this unset and keep rendering nothing for that case, as before.
const StatusIcon = ({ status, showDraftCircle }) => {
  if (status === "missed") {
    return (
      <SvgIcon
        src="/img/new-icons/info-circle.svg"
        classNames={status}
        noFilterOnLight={true}
      />
    );
  }
  if (status === "completed") {
    return (
      <SvgIcon
        src="/img/new-icons/tick-square.svg"
        classNames={status}
        noFilterOnLight={true}
      />
    );
  }
  if (showDraftCircle) {
    return <div className="draft-status-circle" />;
  }
  return null;
};

const OutcomesComponent = (props) => {
  const { t } = useTranslation("dashboard");
  const { locals } = useRillContext();
  // Badge matching needs the wide, retroactive-PR-aware range (a PR is only
  // recognized once enough history exists, which can be later than the day
  // it was actually set) — locals.dayPersonalRecords stays exact-day-scoped
  // for PersonalRecordsCard, which has no date filtering of its own.
  const repMaxPR = locals.dayPersonalRecordsRange?.repMaxPR;

  const {
    exercise,
    set,
    units,
    withWeight,
    fromMetric,
    idx,
    targetLoad,
    targetRM,
    expanded,
    intensityMeasure,
    isRiR,
    hideVelocitySettings,
    showDraftCircle,
  } = props;
  const {
    _id: exerciseId,
    parameters,
    primary,
    repMaximums,
    secondaryRepMaximum,
    type,
    complex,
  } = exercise;

  const showE1RM = shouldHaveE1RM(exercise);
  const showPercentRM = type === "primary" || type === "secondary";
  const defaultRepMax =
    (type === "secondary" || complex) && secondaryRepMaximum
      ? secondaryRepMaximum
      : repMaximums?.[primary] || 0;

  // Only reserve the PR-badge column for exercises that actually have a PR
  // somewhere in one of their sets, otherwise every table gets the extra
  // width for a column that will always be empty.
  const exerciseHasPR =
    !!repMaxPR &&
    exercise.sets?.some((s) =>
      Object.values(s.outcomes || {}).some((o) =>
        isPROutcome(exercise, o, repMaxPR)
      )
    );

  const { total, completed, missed } = getSetCounts(exercise, set);
  const equalOutcomes = outcomesAreEqual(set);

  const isExpanded = (equalOutcomes && expanded) || !equalOutcomes;
  const setsOrRounds = isExpanded ? Number(set.sets || set.rounds || 1) : 1;
  let complexReps;
  if (exercise.exercises?.length) {
    complexReps = getComplexSetReps(exercise.exercises, idx);
  }

  const prevSets = locals.previousOutcomes?.[String(exercise._id)];
  const precision = units === "metric" ? 2 : 1;
  const unitsLabel = units === "metric" ? "kg" : "lb";

  const shownPRReps = new Set();
  return Array.from({ length: setsOrRounds }, (_x, i) => {
    const outcomes = set.outcomes?.[i] || { outcome: {} };

    const { outcome, status } = outcomes;

    const { reps, rpe, load, e1rm, velocity } = outcome || {};

    const prevOutcome = prevSets?.[idx]?.outcomes?.[i]?.outcome;
    const prevReps = prevOutcome?.reps ?? null;
    const prevRpe =
      prevOutcome?.rpe != null && prevOutcome.rpe !== "AMRAP"
        ? Number(prevOutcome.rpe)
        : null;
    const prevLoad =
      prevOutcome?.load != null
        ? round(fromMetric(prevOutcome.load, units), precision)
        : null;
    const dispLoad =
      load != null ? round(fromMetric(load, units), precision) : null;
    const dispRpe = rpe != null && rpe !== "AMRAP" ? Number(rpe) : null;
    const { repsTwo, repsThree } = getComplexSetOutcomeReps(exercise, idx, i);

    const comment = set.comments?.[i] || "";

    const video = set.videos?.[i];

    const { outcomeCount, commentCount, videoCount } = getMetaCounts(set);

    return (
      <div
        className={`actuals-outcomes-container set${i}`}
        key={`setOrRound${i}`}
      >
        <div className="target-status">
          {i === 0 && (
            <>
              <span
                className={
                  completed + missed === total
                    ? "circle-status completed"
                    : missed === 0 && completed === 0
                      ? "circle-status target"
                      : "circle-status partial"
                }
              ></span>
              <p className="p4">
                {formattedTarget(
                  set,
                  showPercentRM,
                  targetRM,
                  units,
                  targetLoad,
                  intensityMeasure,
                  isRiR,
                  false,
                  complexReps
                )}
              </p>
            </>
          )}
        </div>
        <div className="actuals-titles only-show-mobile">
          <p className="p4">{t("calendar.actuals.results")}</p>
          <p className="p4">{t("calendar.actuals.setNumber")}</p>
          {parameters ? (
            Array.from({ length: parameters.length }, (_x, i) =>
              [FREEFORM, ROUNDS, SETS].includes(parameters[i]) ? null : (
                <p
                  key={`${i}-${exercise._id}-title`}
                  className="p4 mobile-hidden"
                >
                  {parameterName(parameters[i], isRiR)}
                </p>
              )
            )
          ) : (
            <>
              <p className="p4">{t("calendar.actuals.reps")}</p>
              <p className="p4">{intensityMeasure}</p>
              <p className="p4">{t("calendar.actuals.load")}</p>
            </>
          )}
          {showPercentRM && (
            <p className="p4 mobile-hidden">
              {t("calendar.actuals.percent1rm")}
            </p>
          )}
          {showE1RM && (
            <p className="p4 mobile-hidden">{t("calendar.actuals.e1rm")}</p>
          )}
          {exerciseHasPR && <p className="p4 pr-badge-cell mobile-hidden"></p>}
          {locals.query?.relTrends !== false && (
            <p className="p4 trend-cell mobile-hidden">
              {t("calendar.actuals.trends")}
            </p>
          )}
          {showPercentRM && !hideVelocitySettings && (
            <p className="p4mobile-hidden">{t("calendar.actuals.velocity")}</p>
          )}
          {parameters && parameters[0] === FREEFORM && (
            <p className="p4 freeform mobile-hidden"></p>
          )}
          <p className="p4 video">{t("calendar.actuals.video")}</p>
          <p className="p4 description mobile-hidden">
            {t("calendar.actuals.comment")}
          </p>
        </div>
        <div className="actuals-outcomes">
          <p className="p4 actuals-status">
            <StatusIcon status={status} showDraftCircle={showDraftCircle} />
          </p>
          <p className="p4">{isExpanded ? i + 1 : `1 - ${outcomeCount}`}</p>
          {parameters ? (
            parameters.map((p) => {
              if (p === ROUNDS || p === SETS) return null;
              const parameter = mapParameter(p);
              const value = outcome ? outcome[parameter] : null;
              if (p === LOAD) {
                return (
                  <p
                    key={`${idx}-set${i}-${exerciseId}-param-${p}`}
                    className="p4 mobile-hidden"
                  >
                    {value
                      ? withWeight(
                          round(fromMetric(value, units, false), 1),
                          units,
                          false
                        )
                      : ""}
                  </p>
                );
              }
              return (
                <p
                  key={`${idx}-set${i}-${exerciseId}-param-${p}`}
                  className={`p4 ${
                    parameter === "freeform" ? "freeform" : ""
                  } mobile-hidden`}
                >
                  {[RPE, RPEAMRAP, "rpe"].includes(parameter)
                    ? convertToRiR(value, !isRiR)
                    : value}
                </p>
              );
            })
          ) : (
            <>
              <p className="p4">
                {reps}
                {repsTwo !== undefined && repsTwo !== null && `+${repsTwo}`}
                {repsThree !== undefined &&
                  repsThree !== null &&
                  `+${repsThree}`}
              </p>
              <p className="p4">{convertToRiR(rpe, !isRiR)}</p>
              <p className="p4">
                {load
                  ? withWeight(
                      round(fromMetric(load, units, false), 1),
                      units,
                      false
                    )
                  : ""}
              </p>
            </>
          )}
          {showPercentRM && (
            <p className="p4 mobile-hidden">
              {load && status === "completed" && defaultRepMax
                ? `${round((load / defaultRepMax) * 100, 1)}%`
                : ""}
            </p>
          )}
          {showE1RM && (
            <p className="p4 mobile-hidden">
              {e1rm && status === "completed"
                ? withWeight(
                    round(fromMetric(e1rm, units, false), 1),
                    units,
                    false
                  )
                : ""}
            </p>
          )}
          {exerciseHasPR &&
            (() => {
              const pr = isPROutcome(exercise, outcomes, repMaxPR);
              const showPR = pr && !shownPRReps.has(pr.reps);
              if (showPR) shownPRReps.add(pr.reps);
              return (
                <p className="p4 pr-badge-cell">
                  {showPR ? <PRBadge reps={pr.reps} /> : ""}
                </p>
              );
            })()}
          {locals.query?.relTrends !== false &&
            (status === "completed" &&
            (prevReps != null || prevRpe != null || prevLoad != null) ? (
              <span className="trend-icons trend-cell">
                <TrendIndicator
                  curr={reps}
                  prev={prevReps}
                  metric="reps"
                  letter="R"
                  label={t("calendar.actuals.reps")}
                />
                <TrendIndicator
                  curr={dispRpe}
                  prev={prevRpe}
                  metric="rpe"
                  letter="E"
                  label={intensityMeasure}
                />
                <TrendIndicator
                  curr={dispLoad}
                  prev={prevLoad}
                  metric="load"
                  letter="L"
                  label={t("calendar.actuals.load")}
                  unit={unitsLabel}
                />
              </span>
            ) : (
              <span className="trend-icons trend-cell" />
            ))}
          {showPercentRM && !hideVelocitySettings && (
            <p className="p4">
              {velocity && status === "completed"
                ? `${getVelocityValue(velocity)}m/s`
                : ""}
            </p>
          )}
          <p className="p4 video">
            {!isExpanded && `${videoCount === 0 ? "-" : videoCount}`}
            {video && isExpanded && (
              <ExerciseVideoLine
                vidItem={video}
                indexIndividualSet={i}
                exerciseToEdit={exercise._id}
                setIndex={idx}
                src="/img/new-icons/play-gradient.svg"
                utcDate={exercise.utcDate}
                primary={exercise.primary ?? exercise.exercise?.primary}
                secondary={exercise.secondary ?? exercise.exercise?.secondary}
                type={exercise.type ?? exercise.exercise?.type}
                movement={exercise.movement ?? exercise.exercise?.movement}
                set={set}
                outcomeEntry={set.outcomes?.[i]}
                setComment={set.comments?.[i]}
              />
            )}
          </p>
          <CommentComponent
            comment={
              !isExpanded
                ? `${commentCount === 0 ? "-" : commentCount}`
                : comment
            }
            truncateLength={24}
          />
        </div>
      </div>
    );
  });
};

export default OutcomesComponent;
